import React from 'react'
import PropTypes from 'prop-types'
import { Box, Tooltip, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { EMPTY_VALUE, boundedRatio, formatQuantityWithUnit, isPartComponent } from '../../../utils/jobDetailFormat'
import { BlockState, DetailSection, HeaderCell, ProgressBar } from '../JobDetailPrimitives'

// Historia 160 (CU5, RN29/RN30, G4, mock 3) — MATERIALES DEL JOB. Las filas van en el orden de D2
// (el de la lista de materiales, RN29); nada se reordena aquí. Pendiente 0 ⇒ "Completo", barra llena
// y color de completado, también con sobreconsumo (D25); la barra nunca pasa del 100 %.
// E-7 (RN55): los componentes pieza de la BOM son filas de ESTA tabla, en su orden. MIC no registra su
// consumo: consumido y pendiente "—" con tooltip, sin barra, ni "Completo" ni color de aviso.

const GRID = 'minmax(0, 0.9fr) minmax(0, 2.4fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 1.1fr)'

const MaterialsTab = ({ block = {}, onRetry = () => {} }) => {
  const { t_, i18n } = useSmartTranslation()
  const language = i18n?.language || 'en'
  const materials = block.data?.materials || []
  return (
    <DetailSection title={t_('Job materials')} testId='materials-block'>
      <BlockState status={block.status} onRetry={onRetry}>
        <Box role='table' aria-label={t_('Job materials')}>
          <Box role='row' sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <HeaderCell>{t_('Code')}</HeaderCell>
            <HeaderCell>{t_('Material')}</HeaderCell>
            <HeaderCell align='right'>{t_('Required quantity')}</HeaderCell>
            <HeaderCell align='right'>{t_('Consumed')}</HeaderCell>
            <HeaderCell align='right'>{t_('Pending quantity')}</HeaderCell>
            <HeaderCell>{t_('Advance')}</HeaderCell>
          </Box>
          {materials.map((material) => {
            const partComponent = isPartComponent(material)
            const complete = !partComponent && material.complete === true
            const tone = partComponent ? 'none' : complete ? 'complete' : 'warning'
            const withoutRecord = (testId) => (
              <Tooltip title={t_('No consumption record')}>
                <Typography role='cell' data-testid={testId} data-tone={tone} sx={{ textAlign: 'right', color: 'text.secondary' }}>{EMPTY_VALUE}</Typography>
              </Tooltip>
            )
            return (
              <Box
                role='row'
                // Las ids de materia prima y de referencia de pieza son de tablas distintas: la clave lleva el tipo.
                key={material.type + '-' + material.rawMaterialReferenceId}
                data-testid={'material-row-' + material.code}
                sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2, minHeight: 54, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
              >
                <Typography role='cell' sx={{ fontFamily: 'monospace' }}>{material.code}</Typography>
                <Typography role='cell' noWrap title={material.name}>{material.name}</Typography>
                <Typography role='cell' sx={{ textAlign: 'right' }}>{formatQuantityWithUnit(material.required, material.unit, language)}</Typography>
                {partComponent
                  ? withoutRecord('material-consumed-' + material.code)
                  : <Typography role='cell' data-testid={'material-consumed-' + material.code} sx={{ textAlign: 'right' }}>{formatQuantityWithUnit(material.consumed, material.unit, language)}</Typography>}
                {partComponent
                  ? withoutRecord('material-pending-' + material.code)
                  : (
                    <Typography
                      role='cell'
                      data-testid={'material-pending-' + material.code}
                      data-tone={tone}
                      sx={{ textAlign: 'right', fontWeight: 800, color: complete ? 'primary.main' : 'yellow.main' }}
                    >
                      {complete ? t_('Complete') : formatQuantityWithUnit(material.pending, material.unit, language)}
                    </Typography>
                    )}
                <Box role='cell'>
                  {partComponent
                    ? null
                    : <ProgressBar value={complete ? 100 : boundedRatio(material.consumed, material.required)} label={material.code} />}
                </Box>
              </Box>
            )
          })}
        </Box>
      </BlockState>
    </DetailSection>
  )
}

MaterialsTab.propTypes = { block: PropTypes.object, onRetry: PropTypes.func }

export default React.memo(MaterialsTab)
